import { useLocation, useNavigate } from "react-router-dom";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { toast, ToastContainer } from "react-toastify";
import { useEffect, useState } from "react";
import { handleNullAnalysis, handlePreProcessing } from "../../modules/fileListModules.ts";
import { CardBody, Input, Row, Table } from "reactstrap";
import { CreatableSelectField } from "../../components/common/CreatableSelectField.tsx";
import SelectField from "../../components/common/SelectField.tsx";
import { CircularProgress } from "@mui/material";
import { queueToast } from "../../components/common/toastMessage.tsx";

const NullHandling = () => {
    const [columns, setColumns] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [config, setConfig] = useState({});
    const [strategies, setStrategies] = useState({});
    const location = useLocation();
    const file = location?.state?.file;
    const navigate = useNavigate();

    const formatStrategyLabel = (value) => {
        return value
            .replace(/_/g, " ")                // forward_fill → forward fill
            .replace(/\b\w/g, char => char.toUpperCase()); // capitalize words
    };

    useEffect(() => {
        const getAnalysis = async () => {
            try {
                setIsLoading(true);

                const response = await handleNullAnalysis(file?.id);

                const cols = response?.analysis?.columns || {};
                setColumns(cols);

                const initialStrategies = {};
                Object.entries(cols).forEach(([key, value]) => {
                    initialStrategies[key] = value.allowed_strategies || [];
                });

                setStrategies(initialStrategies);
            } finally {
                setIsLoading(false);
            }
        };

        getAnalysis();
    }, [file?.id]);
    
    const handleSubmit = async () => {
        if (Object.keys(config).length !== Object.keys(columns).length) {
            toast.error("All columns must have a strategy.");
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await handlePreProcessing(file?.id, { null_config: config });
            queueToast('success', 'Pre-process has been done successfully!');
            navigate(`/files/${file?.dataset_id}`, {
            state: {
                datasetName: location.state.datasetName,
                templateName: location.state.templateName,
                usage: location.state.usage
            }
        });
        } catch (error: any) {
            if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error("Failed to preprocess dataset.");
            }
        } finally {
            setIsSubmitting(false)
        }
    };

    const handleCancel = () => {
        navigate(`/files/${file?.dataset_id}`, {
            state: {
                datasetName: location.state.datasetName,
                templateName: location.state.templateName,
                usage: location.state.usage
            }
        });
    };

    const handleInputChange = (columnKey, inputValue) => {
        setConfig((prev) => {
            const previous = prev[columnKey] || {};

            const updatedColumn = {
                ...previous,
                strategy: inputValue,
            };

            // remove custom value when switching away from custom
            if (inputValue !== "custom") {
                delete updatedColumn.value;
            }

            return {
                ...prev,
                [columnKey]: updatedColumn,
            };
        });
    };

    return (
        <>
            <BreadCrumb moduleName={"Dataset"} pageName={"NullHandling"} viewName={file.file_name} />
            <ToastContainer position="top-center" />
            <div className="listingjs-table " id="customerList">
                <div className="live-preview">
                    <div className="align-items-center d-flex mb-3 ">
                        <h4 className="card-title mb-0 flex-grow-1">Null Values Handling</h4>
                    </div>
                    <>
                        {isLoading ? (
                            <div className="flex justify-center items-center h-64">
                                <CircularProgress sx={{ color: "#757575" }} />
                            </div>
                        ) :
                            (<CardBody>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="table-border border p-3">
                                        <div className=''>
                                            <Table className="table-borderless align-middle table-nowrap ms-0 mb-0 overflow-auto" style={{ tableLayout: "fixed", width: "100%" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "25%" }}>Column Name</th>
                                                        <th style={{ width: "20%" }}>Data Type</th>
                                                        <th style={{ width: "30%" }}>Strategy</th>
                                                        <th style={{ width: "25%" }}>Custom</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Object.entries(columns).map(([key, value]) => (

                                                        <tr key={key}>
                                                            {/* Column Name */}
                                                            <td style={{ width: "25%" }}>
                                                                <div className="bg-light rounded-lg p-2 small text-break overflow-hidden">
                                                                    <div className="text-truncate" title={key}>
                                                                        {key}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Data Type */}
                                                            <td style={{ width: "20%" }}>
                                                                <div className="bg-light rounded-lg p-2 small text-break overflow-hidden">
                                                                    <div className="text-truncate" title={value?.column_type}>
                                                                        {value?.column_type}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Input */}
                                                            <td style={{ width: "30%" }}>
                                                                <SelectField
                                                                    values={
                                                                        config[key]?.strategy
                                                                            ? { label: config[key].strategy, value: config[key].strategy }
                                                                            : null
                                                                    }
                                                                    options={strategies[key]?.map((val) => ({
                                                                        label: formatStrategyLabel(val),
                                                                        value: val,
                                                                    }))}

                                                                    handleChange={(selected) => {
                                                                        handleInputChange(key, selected?.value);
                                                                    }}

                                                                    isClearable={false}
                                                                    isRVStyle={true}
                                                                />
                                                            </td>
                                                            <td style={{ width: "25%" }}>
                                                                <Input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Enter custom value"
                                                                    disabled={config[key]?.strategy !== "custom"}
                                                                    value={config[key]?.value || ""}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;

                                                                        setConfig((prev) => ({
                                                                            ...prev,
                                                                            [key]: {
                                                                                ...prev[key],
                                                                                value: value,
                                                                            },
                                                                        }));
                                                                    }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>

                                            </Table>

                                        </div>
                                    </div>
                                </form>
                            </CardBody>)}
                    </>
                    <Row lg={6} className="justify-content-end mt-4">
                        <div className="live-preview">
                            <div className="d-flex justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
                                    onClick={handleCancel}
                                >
                                    <i className="ri-close-line label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                                    <span className="px-2 ms-2 me-1 fw-bold">
                                        Cancel
                                    </span>
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSubmit();
                                    }}
                                    type="submit"
                                    className="btn-label rv-primary-btn rounded-1 btn btn-secondary d-flex align-items-center"
                                    style={{ height: 'auto', minHeight: 'fit-content' }}
                                    disabled={isSubmitting}
                                >
                                    <i className="ri-play-fill label-icon align-middle fs-16 me-1"></i>
                                    <span className="ms-2 me-1 fw-bold d-inline-flex align-items-center gap-2 text-nowrap">
                                        {isSubmitting ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm text-light" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span>Please wait</span>
                                            </>
                                        ) : (
                                            "Save"
                                        )}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </Row>
                </div>
            </div>
        </>
    );
}
export default NullHandling;